import http from 'src/infrastructure/http'
import { parseFailedResponse, parseGoodResponse } from 'src/infrastructure/parse-axios-response'
import {
  ApproveDisapprovePayload,
  BadgeData,
  CancelQuantityItemPayload,
  CreateInventoryPayload,
  DataPosition,
  DeleteBadgePayload,
  GenericApiErrors,
  GetInventoryPayload,
  GetPositionPayload,
  IdBadgePayload,
  IdPayload,
  IncrementDecrementPayload,
  InventoriesToExecutePayload,
  InventoryDetail,
  InventoryList,
  InventoryProductDetails,
  ConfigsToExecuteInventory,
  ResetAdjustmentApprovalPayload,
  ResponseIncrement,
  SavePositionDamagePayload,
  SavePositionPayload,
  SkusList,
  UnlinkOperatorPayload,
  ValidateSkusPayload,
} from './InventoryTypes'

const getInventory = async (payload: GetInventoryPayload) => {
  try {
    const {
      fulfillmentCenterId, initialDate, finalDate,
    } = payload

    const result = await http.get('/v2/inventory/search', {
      params: {
        id_fulfillment_center: fulfillmentCenterId,
        initial_date: initialDate,
        end_date: finalDate,
      },
    })

    return parseGoodResponse<InventoryList[]>(result.data.inventories)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const createInventory = async (payload: CreateInventoryPayload) => {
  try {
    const {
      name, type, skus, badges, fulfillmentCenterId, lost_locations,
    } = payload

    const body = {
      inventory_name: name,
      count_type: type,
      product_list: skus,
      badge_list: badges,
      id_fulfillment_center: fulfillmentCenterId,
      lost_locations,
    }

    await http.post('/v2/inventory/create-inventory', body)

    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const validateSkus = async (payload: ValidateSkusPayload) => {
  try {
    const { skus } = payload

    const body = { product_ids_list: skus }

    const result = await http.post('/v2/inventory/validate-products', body)

    return parseGoodResponse<SkusList[]>(result.data.products)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const validateAndAddBadge = async (badge: string) => {
  try {
    const result = await http.post('/v2/inventory/badge', { badge })
    return parseGoodResponse<BadgeData>(result.data.badges)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const deleteBadge = async (payload: DeleteBadgePayload) => {
  try {
    const { badge, badgeToDelete } = payload

    await http.delete(`/v2/inventory/badge/${badgeToDelete}`,
      {
        headers: {
          'user-barcode': badge,
          'content-type': 'application/json',
        },
      })
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getBadges = async () => {
  try {
    const result = await http.get('/v2/inventory/badge/all')
    return parseGoodResponse<BadgeData[]>(result.data.badges)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getInventoryDetail = async (payload: IdPayload) => {
  try {
    const { id } = payload

    const result = await http.get(`/v2/inventory/details/${id}`)

    return parseGoodResponse<InventoryDetail>(result.data.inventory_details)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getInventoryProductDetails = async (payload: { inventoryId: number, productCode: string }) => {
  try {
    const { inventoryId, productCode } = payload
    const result = await http.get(`/v2/inventory/details/${inventoryId}/${productCode}`)
    return parseGoodResponse<InventoryProductDetails>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const cancelInventory = async (payload: IdPayload) => {
  try {
    await http.put(`/v2/inventory/cancel-inventory/${payload}`)

    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getInventoriesToExecute = async (payload: InventoriesToExecutePayload) => {
  try {
    const result = await http.get(`/v2/inventory/inventories-to-execute/${payload}`)

    return parseGoodResponse<InventoryList[]>(result.data.inventories)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getConfigsToExecuteInventory = async () => {
  try {
    const result = await http.get('/v2/inventory/configs-to-execute')

    return parseGoodResponse<ConfigsToExecuteInventory>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const validateBadgeToExecuteInventory = async (badge: string, id: number) => {
  try {
    const result = await http.post(`/v2/inventory/badge/validate/${id}`, null, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<DataPosition>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const finishInventory = async (payload: IdPayload) => {
  try {
    await http.post(`/v2/inventory/product/finish/${payload.id}`)
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const incrementDecrementInventory = async (payload: IncrementDecrementPayload) => {
  try {
    const { id, products } = payload

    const productsListFormatted = products.map((product) => ({
      product_code: product.productCode,
      quantity: product.quantity,
    }))

    const body = {
      increment_list: productsListFormatted,
    }
    const result = await http.put(`/v2/inventory/product/increment/${id}`, body)

    return parseGoodResponse<ResponseIncrement>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const approveAdjustment = async (payload: ApproveDisapprovePayload) => {
  try {
    const { idProductInventory, approvalStatus } = payload

    const body = {
      id_product_inventory: idProductInventory,
      approval_status: approvalStatus,
    }
    await http.put('/v2/inventory/product/approval', body)
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const rejectAdjustment = async (payload: ApproveDisapprovePayload) => {
  try {
    const { idProductInventory, approvalStatus } = payload

    const body = {
      id_product_inventory: idProductInventory,
      approval_status: approvalStatus,
    }
    await http.put('/v2/inventory/product/approval', body)
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const resetAdjustmentApproval = async (payload: ResetAdjustmentApprovalPayload) => {
  try {
    const { idProductInventory } = payload
    await http.put(`/v2/inventory/product/reset/approval/${idProductInventory}`)
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getPosition = async (payload: GetPositionPayload) => {
  try {
    const {
      id, floor, addressesTypes, badge, isThirdCount, afterFinishPosition,
    } = payload

    const result = await http.get(`/v2/inventory/position/next/${id}`, {
      params: {
        floor,
        addresses_types: addressesTypes.join(','),
        is_third_count: isThirdCount,
        after_finish_position: afterFinishPosition,
      },
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<DataPosition>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const registerStartPosition = async (payload: IdBadgePayload) => {
  try {
    const { idPosition, badge } = payload

    await http.put(`/v2/inventory/position/start/${idPosition}`, null, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const savePosition = async (payload: SavePositionPayload) => {
  try {
    const {
      idPosition, quantity, expirationDate, badge,
    } = payload

    const body = {
      id_position: idPosition,
      quantity,
      expiration_date: expirationDate,
    }

    await http.post('/v2/inventory/position/count', body, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const savePositionDamage = async (payload: SavePositionDamagePayload) => {
  try {
    const {
      idPosition, quantityDamage, expirationDateDamage, badge,
    } = payload

    const body = {
      id_position: idPosition,
      quantity: quantityDamage,
      expiration_date: expirationDateDamage,
    }

    await http.post('/v2/inventory/position/damage', body, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const cancelQuantityItem = async (payload: CancelQuantityItemPayload) => {
  try {
    const { idPosition, expirationDate, badge } = payload

    const body = {
      id_position: idPosition,
      expiration_date: expirationDate,
    }

    await http.patch('/v2/inventory/position/cancel', body, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const finishPosition = async (payload: IdBadgePayload) => {
  try {
    const { idPosition, badge } = payload

    await http.post(`/v2/inventory/position/finish/${idPosition}`, null, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const unlinkOperator = async (payload: UnlinkOperatorPayload) => {
  try {
    const { idPosition, badge, badgeSupervisor } = payload

    const body = {
      id_position: idPosition,
      badge,
    }

    await http.patch('/v2/inventory/operator/unlink', body, {
      headers: { 'user-barcode': badgeSupervisor },
    })
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

export default {
  getInventory,
  createInventory,
  validateSkus,
  validateAndAddBadge,
  deleteBadge,
  getBadges,
  getInventoryDetail,
  getInventoryProductDetails,
  cancelInventory,
  getInventoriesToExecute,
  getConfigsToExecuteInventory,
  validateBadgeToExecuteInventory,
  finishInventory,
  incrementDecrementInventory,
  approveAdjustment,
  rejectAdjustment,
  resetAdjustmentApproval,
  getPosition,
  registerStartPosition,
  savePosition,
  savePositionDamage,
  cancelQuantityItem,
  finishPosition,
  unlinkOperator,
}
